import { addDays, format, startOfDay } from 'date-fns';
import { homologateDay } from '~/utils/date';

/**
 * El cálculo del informe de ocupación, sin nada de pantalla.
 *
 * Solo para espacios con hora de inicio y de fin (salas). La ocupación es
 * **tiempo**: minutos reservados dentro de la ventana que el administrador
 * mira (por ejemplo lunes a viernes, de 6 am a 7 pm) dividido entre los
 * minutos que la sala estaba disponible en esa misma ventana. Nada de contar
 * reservas: una reserva de 8 horas y otra de media hora no son lo mismo.
 *
 * Decisiones:
 *  · Las horas se leen del texto de la reserva (`startHour`/`endHour`), como
 *    hace el calendario: las marcas de tiempo están en UTC y desplazarían todo.
 *  · El día es el de la reserva en Bogotá (`datetime` pasado a hora local).
 *  · Una reserva cuenta solo por la parte que cae dentro de la ventana: si
 *    va de 18:00 a 21:00 y la ventana acaba a las 19:00, aporta una hora.
 *  · Las canceladas nunca cuentan. "Solo efectivas" deja check-in y check-out.
 *  · La disponibilidad se recorta al horario de la sala ese día de la semana
 *    (claves 0 = lunes, como en toda la app) y es cero si la sala cierra.
 *  · Si dos reservas se solapan en la misma sala, el tiempo se cuenta una
 *    vez (unión de intervalos): la ocupación nunca pasa del 100 %.
 */

export type ReservaOcupacion = {
  id: string;
  code: string;
  nombre: string;
  locationId: string;
  /** El día local, a medianoche. */
  dia: Date;
  /** Minutos desde medianoche. */
  inicio: number;
  fin: number;
  estado: string;
  personas: number;
  monto: number;
};

export type SalaInforme = {
  id: string;
  nombre: string;
  activa: boolean;
  capacidad: number;
  /** Horario por día de la semana, claves 0 = lunes. */
  semana: Record<string, { opening?: string; closing?: string; isOpen?: boolean }>;
  /**
   * Precio por hora configurado (Ajustes → sede → Bloques). null si no hay
   * tarifa. Solo sirve para ESTIMAR lo no vendido (horas libres × precio) y
   * el simulador de meta; lo vendido es siempre lo cobrado de verdad.
   */
  precioHora: number | null;
  /** Descuento (0–1) configurado para reservas de más de 1 h. */
  descuento: number;
};

export type Tarifa = Pick<SalaInforme, 'precioHora' | 'descuento'>;

/**
 * El precio por hora con el que se estima: el configurado, con su descuento.
 * Las horas libres se valoran como si se vendieran en reservas de más de una
 * hora, que es como se vende casi todo (en septiembre de 2026, 95 de las 107
 * reservas de sala duraban más de 1 h); valorarlas a tarifa plena inflaba la
 * estimación. null sin precio.
 */
export const precioEstimado = (t: Tarifa): number | null =>
  t.precioHora !== null && t.precioHora > 0 ? t.precioHora * (1 - t.descuento) : null;

export type Filtros = {
  desde: Date;
  hasta: Date;
  /** Días de la semana, 0 = lunes … 6 = domingo. */
  dias: number[];
  /** Ventana horaria, en minutos desde medianoche. */
  horaDesde: number;
  horaHasta: number;
  /** Ids de las salas que entran. */
  salas: string[];
  soloEfectivas: boolean;
  /**
   * El día de hoy. En modo "efectivas" solo cuentan los días ya terminados:
   * ni hoy ni los posteriores entran como disponibles ni como ocupados. En un
   * día que no ha llegado nadie ha podido hacer check-in, y contarlo como
   * disponible hundía el porcentaje (pasó en producción: 514 h de 3.978 h con
   * un período que acababa un mes después); hoy, a medias, hacía lo mismo a
   * menor escala (a las 4 pm iba por el 16 % contra el 40 % de los demás
   * días). En modo "reservadas" hoy y el futuro sí cuentan: es lo reservado.
   */
  hoy?: Date;
};

export type Intervalo = { inicio: number; fin: number };

export const aMinutos = (hora: string): number | null => {
  const m = /^(\d{1,2}):(\d{2})/.exec((hora ?? '').trim());
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
};

/** '08:00 AM' → '08:00'. Las reservas antiguas guardan la hora así. */
export const normalizarHora = (valor?: string | null): string | null => {
  if (!valor) return null;
  const t = String(valor).trim();
  const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i.exec(t);
  if (!m) return null;
  let h = Number(m[1]);
  const periodo = m[3]?.toUpperCase();
  if (periodo === 'PM' && h < 12) h += 12;
  if (periodo === 'AM' && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${m[2]}`;
};

export const minutosAHora = (min: number): string =>
  `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;

/** "6 am", "7 pm", "9:30 am": como lo diría una persona. */
export const horaCorta = (min: number): string => {
  const h = Math.floor(min / 60);
  const m = min % 60;
  const periodo = h < 12 || h === 24 ? 'am' : 'pm';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12} ${periodo}` : `${h12}:${String(m).padStart(2, '0')} ${periodo}`;
};

/** Día de la semana con 0 = lunes. */
export const diaSemana = (d: Date): number => homologateDay[d.getDay()];

export const NOMBRES_DIAS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
/** Una letra por día, como en el resto de la app: la X es el miércoles. */
export const LETRAS_DIAS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

/** Cuánto se solapan dos intervalos, en minutos (0 si nada). */
const solape = (a: Intervalo, b: Intervalo): number =>
  Math.max(0, Math.min(a.fin, b.fin) - Math.max(a.inicio, b.inicio));

/** Minutos cubiertos por la unión de varios intervalos (los solapados se cuentan una vez). */
export const minutosUnion = (intervalos: Intervalo[]): number => {
  const ordenados = [...intervalos].filter((i) => i.fin > i.inicio).sort((a, b) => a.inicio - b.inicio);
  let total = 0;
  let actual: Intervalo | null = null;
  for (const i of ordenados) {
    if (!actual || i.inicio > actual.fin) {
      if (actual) total += actual.fin - actual.inicio;
      actual = { ...i };
    } else if (i.fin > actual.fin) {
      actual.fin = i.fin;
    }
  }
  if (actual) total += actual.fin - actual.inicio;
  return total;
};

/** Horario de una sala un día concreto, recortado a la ventana; null si cierra. */
export const disponibleEnElDia = (
  sala: SalaInforme,
  dia: Date,
  ventana: Intervalo
): Intervalo | null => {
  const horario = sala.semana?.[diaSemana(dia)];
  if (horario && horario.isOpen === false) return null;
  const apertura = aMinutos(horario?.opening ?? '') ?? 0;
  const cierre = aMinutos(horario?.closing ?? '') ?? 24 * 60;
  const inicio = Math.max(apertura, ventana.inicio);
  const fin = Math.min(cierre, ventana.fin);
  return fin > inicio ? { inicio, fin } : null;
};

/** Los días del rango que caen en los días de la semana elegidos. */
export const diasDelRango = (desde: Date, hasta: Date, dias: number[]): Date[] => {
  const lista: Date[] = [];
  const fin = startOfDay(hasta).getTime();
  for (let d = startOfDay(desde); d.getTime() <= fin; d = addDays(d, 1)) {
    if (dias.includes(diaSemana(d))) lista.push(d);
  }
  return lista;
};

export const claveDia = (d: Date): string => format(d, 'yyyy-MM-dd');

export type ResultadoSala = {
  sala: SalaInforme;
  disponibleMin: number;
  ocupadoMin: number;
  /** 0–1; null si la sala no tuvo ni un minuto disponible en la ventana. */
  ocupacion: number | null;
  reservas: number;
  personas: number;
  vendido: number;
  conVenta: number;
  /** Personas de las reservas con venta (para el ticket por persona). */
  personasConVenta: number;
  ticketPorPersona: number | null;
  /** Horas libres de la sala en la ventana (disponibles − reservadas), en minutos. */
  libresMin: number;
  /**
   * Minutos de las reservas contadas que caen fuera de la ventana o del
   * horario de la sala (una reserva de 6 a 18 con ventana 8–18 deja 120
   * aquí). Solo para explicarlo: no entran en `ocupadoMin`.
   */
  fueraMin: number;
  /** Si la sala tiene precio por hora configurado. */
  conTarifa: boolean;
  /** Horas libres × precio estimado por hora (con el descuento). null sin tarifa. */
  noVendidoTarifa: number | null;
};

export type CeldaCalor = {
  dia: number;
  /** Hora de inicio del tramo, en minutos. */
  hora: number;
  disponibleMin: number;
  ocupadoMin: number;
  ocupacion: number | null;
  reservas: number;
};

export type PuntoDia = {
  dia: Date;
  clave: string;
  disponibleMin: number;
  ocupadoMin: number;
  ocupacion: number | null;
  reservas: number;
  vendido: number;
};

export type PuntoHora = {
  hora: number;
  disponibleMin: number;
  ocupadoMin: number;
  ocupacion: number | null;
};

export type Resultado = {
  ocupacion: number | null;
  disponibleMin: number;
  ocupadoMin: number;
  reservas: number;
  personas: number;
  vendido: number;
  conVenta: number;
  /** Valor vendido entre las personas de las reservas con venta (como el informe de siempre). */
  ticketPorPersona: number | null;
  /** Minutos de las reservas contadas que quedaron fuera de la ventana (suma de las salas). */
  fueraMin: number;
  /** Suma de horas libres × precio estimado de las salas con tarifa. null si ninguna la tiene. */
  noVendidoTarifa: number | null;
  /** Salas elegidas sin precio por hora configurado. */
  salasSinTarifa: number;
  diasContados: number;
  /** Días del período que quedaron fuera por ser hoy o futuros (solo en modo efectivas). */
  diasFuturosFuera: number;
  porSala: ResultadoSala[];
  calor: CeldaCalor[];
  porDia: PuntoDia[];
  porHora: PuntoHora[];
  /** Las reservas que entraron, por si se quieren listar. */
  incluidas: ReservaOcupacion[];
};

const cuenta = (r: ReservaOcupacion, soloEfectivas: boolean): boolean => {
  if (r.estado === 'cancelled') return false;
  if (soloEfectivas) return r.estado === 'checkin' || r.estado === 'checkout';
  return true;
};

const porcentaje = (ocupado: number, disponible: number): number | null =>
  disponible > 0 ? Math.min(1, ocupado / disponible) : null;

export const calcularOcupacion = (
  reservas: ReservaOcupacion[],
  salas: SalaInforme[],
  filtros: Filtros
): Resultado => {
  const ventana: Intervalo = { inicio: filtros.horaDesde, fin: filtros.horaHasta };
  const salasElegidas = salas.filter((s) => filtros.salas.includes(s.id));
  const todosLosDias = diasDelRango(filtros.desde, filtros.hasta, filtros.dias);
  // Efectivas: solo días terminados, es decir, estrictamente antes de hoy.
  const tope = filtros.soloEfectivas && filtros.hoy ? startOfDay(filtros.hoy).getTime() : Infinity;
  const dias = todosLosDias.filter((d) => d.getTime() < tope);
  const diasFuturosFuera = todosLosDias.length - dias.length;
  const clavesDias = new Set(dias.map(claveDia));

  // Solo las reservas de las salas y días elegidos que pisan la ventana.
  const incluidas = reservas.filter(
    (r) =>
      filtros.salas.includes(r.locationId) &&
      clavesDias.has(claveDia(r.dia)) &&
      cuenta(r, filtros.soloEfectivas) &&
      solape(r, ventana) > 0
  );

  // Agrupadas por sala y día, para hacer la unión de intervalos.
  const porSalaDia = new Map<string, ReservaOcupacion[]>();
  incluidas.forEach((r) => {
    const k = `${r.locationId}|${claveDia(r.dia)}`;
    porSalaDia.set(k, [...(porSalaDia.get(k) ?? []), r]);
  });

  // Tramos de una hora dentro de la ventana (el último puede ser más corto).
  const tramos: Intervalo[] = [];
  for (let h = ventana.inicio; h < ventana.fin; h += 60) {
    tramos.push({ inicio: h, fin: Math.min(h + 60, ventana.fin) });
  }

  const porSala: ResultadoSala[] = [];
  const calorMapa = new Map<string, CeldaCalor>();
  const porDiaMapa = new Map<string, PuntoDia>();
  const porHoraMapa = new Map<number, PuntoHora>();
  let disponibleTotal = 0;
  let ocupadoTotal = 0;

  const celda = (dia: number, hora: number): CeldaCalor => {
    const k = `${dia}|${hora}`;
    let c = calorMapa.get(k);
    if (!c) {
      c = { dia, hora, disponibleMin: 0, ocupadoMin: 0, ocupacion: null, reservas: 0 };
      calorMapa.set(k, c);
    }
    return c;
  };
  const puntoDia = (d: Date): PuntoDia => {
    const k = claveDia(d);
    let p = porDiaMapa.get(k);
    if (!p) {
      p = { dia: d, clave: k, disponibleMin: 0, ocupadoMin: 0, ocupacion: null, reservas: 0, vendido: 0 };
      porDiaMapa.set(k, p);
    }
    return p;
  };
  const puntoHora = (h: number): PuntoHora => {
    let p = porHoraMapa.get(h);
    if (!p) {
      p = { hora: h, disponibleMin: 0, ocupadoMin: 0, ocupacion: null };
      porHoraMapa.set(h, p);
    }
    return p;
  };

  for (const sala of salasElegidas) {
    let disponibleSala = 0;
    let ocupadoSala = 0;
    let fueraSala = 0;
    const suyas = incluidas.filter((r) => r.locationId === sala.id);

    for (const d of dias) {
      const abierto = disponibleEnElDia(sala, d, ventana);
      if (!abierto) continue;
      const dd = diaSemana(d);
      const delDia = porSalaDia.get(`${sala.id}|${claveDia(d)}`) ?? [];
      const recortadas = delDia.map((r) => ({
        inicio: Math.max(r.inicio, abierto.inicio),
        fin: Math.min(r.fin, abierto.fin),
      }));
      const ocupadoDia = minutosUnion(recortadas);
      const disponibleDia = abierto.fin - abierto.inicio;
      disponibleSala += disponibleDia;
      ocupadoSala += ocupadoDia;
      fueraSala += delDia.reduce((a, r) => a + (r.fin - r.inicio) - solape(r, abierto), 0);

      const pd = puntoDia(d);
      pd.disponibleMin += disponibleDia;
      pd.ocupadoMin += ocupadoDia;
      pd.reservas += delDia.length;
      pd.vendido += delDia.reduce((a, r) => a + r.monto, 0);

      for (const tramo of tramos) {
        const abiertoEnTramo = solape(abierto, tramo);
        if (abiertoEnTramo <= 0) continue;
        const enTramo = recortadas
          .map((i) => ({ inicio: Math.max(i.inicio, tramo.inicio), fin: Math.min(i.fin, tramo.fin) }))
          .filter((i) => i.fin > i.inicio);
        const ocupadoTramo = minutosUnion(enTramo);
        const c = celda(dd, tramo.inicio);
        c.disponibleMin += abiertoEnTramo;
        c.ocupadoMin += ocupadoTramo;
        c.reservas += enTramo.length;
        const ph = puntoHora(tramo.inicio);
        ph.disponibleMin += abiertoEnTramo;
        ph.ocupadoMin += ocupadoTramo;
      }
    }

    const conVenta = suyas.filter((r) => r.monto > 0);
    const vendidoSala = conVenta.reduce((a, r) => a + r.monto, 0);
    const personasConVentaSala = conVenta.reduce((a, r) => a + r.personas, 0);
    porSala.push({
      sala,
      disponibleMin: disponibleSala,
      ocupadoMin: ocupadoSala,
      ocupacion: porcentaje(ocupadoSala, disponibleSala),
      reservas: suyas.length,
      personas: suyas.reduce((a, r) => a + r.personas, 0),
      vendido: vendidoSala,
      conVenta: conVenta.length,
      personasConVenta: personasConVentaSala,
      ticketPorPersona: personasConVentaSala > 0 ? vendidoSala / personasConVentaSala : null,
      libresMin: Math.max(0, disponibleSala - ocupadoSala),
      fueraMin: fueraSala,
      conTarifa: precioEstimado(sala) !== null,
      noVendidoTarifa:
        precioEstimado(sala) !== null
          ? (Math.max(0, disponibleSala - ocupadoSala) / 60) * (precioEstimado(sala) ?? 0)
          : null,
    });
    disponibleTotal += disponibleSala;
    ocupadoTotal += ocupadoSala;
  }

  const calor = [...calorMapa.values()].map((c) => ({
    ...c,
    ocupacion: porcentaje(c.ocupadoMin, c.disponibleMin),
  }));
  const porDia = [...porDiaMapa.values()]
    .map((p) => ({ ...p, ocupacion: porcentaje(p.ocupadoMin, p.disponibleMin) }))
    .sort((a, b) => a.dia.getTime() - b.dia.getTime());
  const porHora = [...porHoraMapa.values()]
    .map((p) => ({ ...p, ocupacion: porcentaje(p.ocupadoMin, p.disponibleMin) }))
    .sort((a, b) => a.hora - b.hora);

  const conVenta = incluidas.filter((r) => r.monto > 0);
  const vendido = conVenta.reduce((a, r) => a + r.monto, 0);
  const personasConVenta = conVenta.reduce((a, r) => a + r.personas, 0);
  // Arriba, la suma de las salas con tarifa: la misma cuenta que se ve en cada fila.
  const conTarifa = porSala.filter((s) => s.conTarifa);
  const noVendidoTarifa = conTarifa.length ? conTarifa.reduce((a, s) => a + (s.noVendidoTarifa ?? 0), 0) : null;

  return {
    ocupacion: porcentaje(ocupadoTotal, disponibleTotal),
    disponibleMin: disponibleTotal,
    ocupadoMin: ocupadoTotal,
    reservas: incluidas.length,
    personas: incluidas.reduce((a, r) => a + r.personas, 0),
    vendido,
    conVenta: conVenta.length,
    ticketPorPersona: personasConVenta > 0 ? vendido / personasConVenta : null,
    fueraMin: porSala.reduce((a, s) => a + s.fueraMin, 0),
    noVendidoTarifa,
    salasSinTarifa: porSala.filter((s) => !s.conTarifa).length,
    diasContados: dias.length,
    diasFuturosFuera,
    porSala: porSala.sort((a, b) => (b.ocupacion ?? -1) - (a.ocupacion ?? -1)),
    calor,
    porDia,
    porHora,
    incluidas,
  };
};

/* ─────────────────────────  simulador de meta  ───────────────────────── */

export type SimulacionSala = {
  sala: SalaInforme;
  disponibleMin: number;
  ocupadoMin: number;
  ocupacionActual: number | null;
  /** Ocupación de la sala si se hubiera llegado a la meta (0–1); null sin minutos disponibles. */
  ocupacionConMeta: number | null;
  /** Minutos de más respecto a lo que hubo. */
  deMasMin: number;
  /** Tarifa con la que se simuló esta sala (la configurada o la de prueba). */
  tarifa: Tarifa;
  /** Precio por hora con el que se valoran las horas de más (con descuento); null sin tarifa. */
  precioEstimado: number | null;
  /** Lo cobrado de verdad en el período (lo mismo que la columna «Vendido»). */
  vendido: number;
  /** Valor puesto a mano a las reservas que vienen de esta sala (0 si no se puso). Estimación. */
  estimadoVienen: number;
  /** Horas de más × precio estimado. null sin tarifa. */
  ventaDeMas: number | null;
  /** vendido + estimadoVienen + ventaDeMas. null sin tarifa. */
  totalConMeta: number | null;
};

export type Simulacion = {
  /** La meta general aplicada (0–1): nunca menor que la ocupación que hubo. */
  meta: number;
  /** "general": la meta se reparte; "porSala": cada sala tiene la suya y la general sale de la suma. */
  modo: 'general' | 'porSala';
  ocupacionActual: number | null;
  /** Puntos porcentuales de diferencia entre la meta y lo que hubo (0–100). */
  puntosDeMas: number;
  disponibleMin: number;
  ocupadoMin: number;
  deMasMin: number;
  /** Lo cobrado de verdad, suma de todas las salas. */
  vendido: number;
  /** Suma de lo puesto a mano a las reservas que vienen (0 si no se puso). Estimación. */
  estimadoVienen: number;
  /** Suma de las salas con tarifa. */
  ventaDeMas: number;
  totalConMeta: number;
  /** Salas sin precio por hora: sus horas cuentan, su venta no se puede calcular. */
  salasSinTarifa: number;
  porSala: SimulacionSala[];
};

export type OpcionesSimulacion = {
  /**
   * Meta propia de cada sala (0–1, por id). Si viene, manda sobre la meta
   * general: cada sala sube hasta la suya (nunca baja de lo que hubo) y la
   * general se recalcula con la suma. Las salas que no estén en el mapa se
   * quedan como estuvieron.
   */
  metasSala?: Record<string, number> | null;
  /** Tarifas de prueba por id de sala: sustituyen a las configuradas sin guardarlas. */
  tarifas?: Record<string, Tarifa> | null;
  /**
   * Valor estimado de las reservas que vienen, sumado por id de sala (ver
   * `reservasQueVienen`). Hoy valen $0 porque se cobran en el check-out; esto
   * deja simular con lo que se espera cobrarles. Nunca toca lo vendido.
   */
  estimadoVienen?: Record<string, number> | null;
};

/**
 * "¿Y si hubiéramos llegado al X %?": qué habría pasado en el mismo período,
 * con los mismos filtros, con una ocupación mayor de la que hubo.
 *
 * La meta nunca baja de lo que ya pasó: lo reservado, reservado está. Con una
 * meta general, las horas de más que pide se reparten entre las salas en
 * proporción a las horas libres de cada una (la que más libre estuvo, más
 * habría podido crecer), de modo que las filas suman exactamente el total en
 * horas y en pesos, y al 100 % cada sala se llena. Con metas por sala, cada
 * una sube hasta la suya y la general sale de la suma. Lo vendido es lo
 * cobrado de verdad; solo lo de más es una estimación, al precio por hora con
 * su descuento (`precioEstimado`), así que al 100 % la venta de más coincide
 * con «No vendido (tarifa)». El valor puesto a las reservas que vienen
 * (`estimadoVienen`) es otra estimación, aparte: se suma al total, nunca a lo
 * vendido, y no cambia horas ni ocupación (esas reservas ya están contadas).
 */
export const simularMeta = (
  resultado: Resultado,
  metaPedida: number,
  opciones: OpcionesSimulacion = {}
): Simulacion => {
  const actual = resultado.ocupacion ?? 0;
  const porSalaMetas = opciones.metasSala && Object.keys(opciones.metasSala).length > 0 ? opciones.metasSala : null;
  const libresTotal = Math.max(0, resultado.disponibleMin - resultado.ocupadoMin);

  // Cuántos minutos de más le tocan a cada sala.
  let deMasPorSala: (s: ResultadoSala) => number;
  if (porSalaMetas) {
    deMasPorSala = (s) => {
      const metaSala = porSalaMetas[s.sala.id];
      if (metaSala === undefined || s.disponibleMin <= 0) return 0;
      return Math.min(s.libresMin, Math.max(0, s.disponibleMin * Math.min(1, metaSala) - s.ocupadoMin));
    };
  } else {
    const meta = Math.min(1, Math.max(metaPedida, actual));
    const deMasTotal = Math.min(libresTotal, Math.max(0, resultado.disponibleMin * meta - resultado.ocupadoMin));
    // Qué fracción de sus horas libres llena cada sala: la misma para todas.
    const fraccion = libresTotal > 0 ? deMasTotal / libresTotal : 0;
    deMasPorSala = (s) => s.libresMin * fraccion;
  }

  const porSala: SimulacionSala[] = resultado.porSala.map((s) => {
    const deMasMin = deMasPorSala(s);
    const tarifa: Tarifa = opciones.tarifas?.[s.sala.id] ?? s.sala;
    const precio = precioEstimado(tarifa);
    const ventaDeMas = precio === null ? null : (deMasMin / 60) * precio;
    const estimadoVienen = Math.max(0, opciones.estimadoVienen?.[s.sala.id] ?? 0);
    return {
      sala: s.sala,
      disponibleMin: s.disponibleMin,
      ocupadoMin: s.ocupadoMin,
      ocupacionActual: s.ocupacion,
      ocupacionConMeta: s.disponibleMin > 0 ? Math.min(1, (s.ocupadoMin + deMasMin) / s.disponibleMin) : null,
      deMasMin,
      tarifa,
      precioEstimado: precio,
      vendido: s.vendido,
      estimadoVienen,
      ventaDeMas,
      totalConMeta: ventaDeMas === null ? null : s.vendido + estimadoVienen + ventaDeMas,
    };
  });
  const deMasTotal = porSala.reduce((a, s) => a + s.deMasMin, 0);
  const meta = porSalaMetas
    ? resultado.disponibleMin > 0
      ? Math.min(1, (resultado.ocupadoMin + deMasTotal) / resultado.disponibleMin)
      : 0
    : Math.min(1, Math.max(metaPedida, actual));
  const vendido = porSala.reduce((a, s) => a + s.vendido, 0);
  const estimadoVienen = porSala.reduce((a, s) => a + s.estimadoVienen, 0);
  const ventaDeMas = porSala.reduce((a, s) => a + (s.ventaDeMas ?? 0), 0);
  return {
    meta,
    modo: porSalaMetas ? 'porSala' : 'general',
    ocupacionActual: resultado.ocupacion,
    puntosDeMas: resultado.ocupacion === null ? 0 : Math.max(0, (meta - actual) * 100),
    disponibleMin: resultado.disponibleMin,
    ocupadoMin: resultado.ocupadoMin,
    deMasMin: deMasTotal,
    vendido,
    estimadoVienen,
    ventaDeMas,
    totalConMeta: vendido + estimadoVienen + ventaDeMas,
    salasSinTarifa: porSala.filter((s) => s.disponibleMin > 0 && s.ventaDeMas === null).length,
    porSala,
  };
};

/* ─────────────────────────  reservas que vienen  ───────────────────────── */

/**
 * Las reservas del resultado que todavía no se han cobrado porque aún no han
 * pasado: de hoy en adelante, sin check-out y sin valor anotado. El valor se
 * anota en el check-out, así que hasta entonces ocupan horas pero suman $0 a
 * «Vendido» (el 23/09/2026, las 87 reservas de sala de NOI desde ese día
 * hasta diciembre no tenían ni el campo de pago).
 *
 * Solo aparecen con «Reservadas»: en «Efectivas» ni hoy ni el futuro entran.
 * Las de días pasados sin check-out no están a propósito: ya pasaron, y lo
 * que ya pasó no se estima.
 */
export const reservasQueVienen = (resultado: Resultado, hoy: Date): ReservaOcupacion[] => {
  const desde = startOfDay(hoy).getTime();
  return resultado.incluidas
    .filter((r) => r.dia.getTime() >= desde && r.estado !== 'checkout' && r.monto <= 0)
    .sort((a, b) => a.dia.getTime() - b.dia.getTime() || a.inicio - b.inicio);
};

export type ValorParaTodas = {
  /** "hora": el valor es por hora de la reserva; "reserva": el mismo valor para cada una. */
  modo: 'hora' | 'reserva';
  valor: number;
  /** 0–1. Solo por hora, y solo a las reservas de más de 1 hora (como la tarifa de las salas). */
  descuento: number;
};

/**
 * Lo que valdría una reserva que viene con un valor puesto a todas. Por hora
 * se cuentan las horas completas de la reserva, no solo las que caen en la
 * ventana: lo que se cobra es la reserva entera (igual que «Vendido»).
 */
export const valorEstimadoReserva = (r: ReservaOcupacion, v: ValorParaTodas): number => {
  if (!(v.valor > 0)) return 0;
  if (v.modo === 'reserva') return Math.round(v.valor);
  const horas = (r.fin - r.inicio) / 60;
  const descuento = horas > 1 ? Math.min(1, Math.max(0, v.descuento)) : 0;
  return Math.round(horas * v.valor * (1 - descuento));
};

export type ReferenciaCobro = {
  /** Lo cobrado ÷ las horas completas de las reservas con cobro. null si ninguna tuvo cobro. */
  porHora: number | null;
  /** Lo cobrado ÷ las reservas con cobro. */
  porReserva: number | null;
  conCobro: number;
  /** Reservas terminadas (check-out) que se cerraron en $0. */
  cerradasEnCero: number;
};

/**
 * Lo que se cobró de verdad en el período, como referencia para poner valor a
 * las que vienen. Sale ya con los descuentos que se hayan hecho. Las cerradas
 * en $0 se cuentan aparte porque pesan: entre julio y septiembre de 2026 fueron
 * 284 de 428 check-outs de sala, y dar valor a todas las que vienen supone que
 * todas pagarán.
 */
export const referenciaDeCobro = (resultado: Resultado): ReferenciaCobro => {
  const conCobro = resultado.incluidas.filter((r) => r.monto > 0);
  const total = conCobro.reduce((a, r) => a + r.monto, 0);
  const horas = conCobro.reduce((a, r) => a + (r.fin - r.inicio) / 60, 0);
  return {
    porHora: horas > 0 ? total / horas : null,
    porReserva: conCobro.length ? total / conCobro.length : null,
    conCobro: conCobro.length,
    cerradasEnCero: resultado.incluidas.filter((r) => r.estado === 'checkout' && r.monto <= 0).length,
  };
};
